import { Divider } from '@design-system';
import { FC, useEffect, useMemo } from 'react';

import { INSIGHTS_INDEX_PAGE_TYPE } from '../../constants';
import { AuthEntryPoint } from '../../features/auth/types';
import { FranchiseIndex } from '../../types/Franchises';
import { TrackPageView } from '../../utils/analytics';
import { useAnalyticsEmitter } from '../../utils/analytics/emitter';
import { InsightItem } from '../../utils/journals/insights';
import PageHeading from '../Layout/PageHeading';
import PageSection from '../Layout/PageSection';
import { SubscriptionBanner } from '../Subscriptions';
import { FranchiseEntrypoints } from './FranchiseEntrypoints';
import { InsightsGrid } from './InsightsGrid';

interface InsightsPageProps {
  locale: string;
  index: FranchiseIndex;
  indexCounts: number[];
  pagination: {
    page: number;
    perPage: number;
    total: number;
  };
  stories: InsightItem[];
}

export const InsightsPage: FC<InsightsPageProps> = ({
  index,
  indexCounts,
  locale,
  pagination,
  stories,
}) => {
  const [subscription] = index.content.subscription || [];

  const properties = useMemo(
    () => ({
      'articles count': stories.length,
      locale,
      pagination,
    }),
    [locale, pagination, stories.length],
  );

  const [analyticsEmitter] = useAnalyticsEmitter(properties);

  useEffect(() => {
    TrackPageView({
      page: INSIGHTS_INDEX_PAGE_TYPE,
      title: index.content.title,
      properties,
    });
  }, [locale, index, properties]);

  return (
    <main className="my-9 space-y-6 md:my-12">
      <PageSection>
        <PageHeading
          heading={index.content.title}
          strapline={index.content.subtitle}
        />
      </PageSection>

      {index.content.entrypoints.length > 0 && (
        <PageSection fullWidth>
          <FranchiseEntrypoints
            entrypoints={index.content.entrypoints}
            indexCounts={indexCounts}
          />
        </PageSection>
      )}

      {index.content.entrypoints.length > 0 && (
        <div className="wrapper-website">
          <Divider className="my-9 md:my-12" />
        </div>
      )}

      <PageSection>
        <InsightsGrid
          handleArticleClick={(article, pos) => {
            analyticsEmitter('insights', 'article click', {
              position: pos + 1,
              'article slug': article.full_slug,
              'article tag': article.tag,
              'article title': article.title,
            });
          }}
          handlePaginationClick={(page) => {
            analyticsEmitter('insights', 'pagination click', {
              nextPage: page,
              pagination,
            });
          }}
          pagination={pagination}
          stories={stories}
        />
      </PageSection>

      {subscription && (
        <div className="wrapper-website">
          <Divider className="my-9 md:my-12" />
        </div>
      )}

      {subscription && (
        <PageSection>
          <SubscriptionBanner
            buttonLocation="insights index"
            image={subscription.image.filename}
            source={AuthEntryPoint.NewsletterInsights}
            subtitle={subscription.subtitle}
            title={subscription.title}
          />
        </PageSection>
      )}
    </main>
  );
};
